import { Component, Input, OnChanges, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { BenefitFlyer, BenefitFlyersService } from '../../services/teams/benefit-flyers.service';
import { SessionService } from '../../services/session/session.service';

@Component({
  selector: 'app-benefit-flyers',
  templateUrl: './benefit-flyers.component.html',
  styleUrls: ['./benefit-flyers.component.scss'],
  imports: [FormsModule, MatButtonModule, MatIconModule, MatMenuModule, MatFormFieldModule, MatInputModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BenefitFlyersComponent implements OnChanges, OnDestroy {
  @Input() teamId: string | undefined;
  @Input() compact = false;
  flyers: BenefitFlyer[] = [];
  loading = false;
  busy = false;
  error = '';
  displayName = '';
  file?: File;
  replacing?: BenefitFlyer;
  private version = 0;
  private loadVersion = 0;
  private subscription: Subscription;

  private service = inject(BenefitFlyersService);
  private session = inject(SessionService);
  private changeDetector = inject(ChangeDetectorRef);

  constructor() {
    this.subscription = this.service.changed.subscribe(teamId => {
      if (teamId === this.teamId) void this.load();
    });
  }
  get canManage(): boolean { return !this.compact && this.session.isExactSuperAdmin(); }
  ngOnChanges(): void {
    this.version++;
    this.flyers = [];
    this.error = '';
    this.busy = false;
    this.resetForm();
    void this.load();
  }
  ngOnDestroy(): void { this.version++; this.loadVersion++; this.subscription.unsubscribe(); }
  async load(): Promise<void> {
    const version = this.version;
    const loadVersion = ++this.loadVersion;
    this.loading = false;
    if (!this.teamId || this.teamId === 'current') return;
    this.loading = true;
    this.error = '';
    this.changeDetector.markForCheck();
    try {
      const flyers = await this.service.list(this.teamId);
      if (version === this.version && loadVersion === this.loadVersion) this.flyers = flyers;
    } catch (error: any) {
      if (version === this.version && loadVersion === this.loadVersion) {
        this.flyers = [];
        this.error = this.message(error, 'Unable to load Benefits-at-a-glance.');
      }
    } finally {
      if (version === this.version && loadVersion === this.loadVersion) {
        this.loading = false;
        this.changeDetector.markForCheck();
      }
    }
  }
  selectFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.file = input.files?.[0];
    input.value = '';
    this.error = '';
    if (this.file && (!/\.pdf$/i.test(this.file.name) || !this.file.size || this.file.size > 10 * 1024 * 1024)) {
      this.file = undefined;
      this.error = 'Choose a nonempty PDF of 10 MB or smaller.';
    }
  }
  replace(flyer: BenefitFlyer): void {
    this.resetForm();
    this.replacing = flyer;
    this.displayName = flyer.displayName;
  }
  resetForm(): void { this.file = undefined; this.replacing = undefined; this.displayName = ''; }
  async save(): Promise<void> {
    if (!this.canManage || !this.teamId || !this.file || !this.displayName.trim() || this.busy) return;
    const version = this.version;
    const teamId = this.teamId;
    this.busy = true;
    this.error = '';
    try {
      await this.service.upload(teamId, this.displayName.trim(), this.file, this.replacing?.id);
      if (version === this.version) this.resetForm();
    } catch (error: any) {
      if (version === this.version) this.error = this.message(error, 'Unable to save flyer.');
    } finally { if (version === this.version) { this.busy = false; this.changeDetector.markForCheck(); } }
  }
  async remove(flyer: BenefitFlyer): Promise<void> {
    if (!this.canManage || !this.teamId || this.busy || !window.confirm(`Remove "${flyer.displayName}" from this wellspace?`)) return;
    const version = this.version;
    this.busy = true;
    this.error = '';
    try {
      await this.service.remove(this.teamId, flyer.id);
      if (version === this.version && this.replacing?.id === flyer.id) this.resetForm();
    } catch (error: any) {
      if (version === this.version) this.error = this.message(error, 'Unable to remove flyer.');
    } finally { if (version === this.version) { this.busy = false; this.changeDetector.markForCheck(); } }
  }
  async download(flyer: BenefitFlyer): Promise<void> {
    if (!this.teamId || this.busy) return;
    const version = this.version;
    this.busy = true;
    this.error = '';
    try {
      const result = await this.service.download(this.teamId, flyer.id);
      if (version !== this.version) return;
      const link = document.createElement('a');
      link.href = result.url;
      link.download = flyer.filename;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error: any) {
      if (version === this.version) this.error = this.message(error, 'Unable to download flyer.');
    } finally { if (version === this.version) { this.busy = false; this.changeDetector.markForCheck(); } }
  }
  private message(error: any, defaultMessage: string): string {
    return typeof error?.error?.error === 'string' ? error.error.error : defaultMessage;
  }
}
